/**
 * App Component (Web)
 * Main application entry point with routing
 * Phase 2: Authentication & User Management
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { Provider } from 'react-redux';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import React from 'react';
import { store } from '@shared/store/index';
import { useAuthInit } from '@shared/features/auth/hooks/useAuthInit';
import { marketDataWS } from '@shared/api/marketDataWebSocket';
import { LoginPage } from './features/auth/LoginPage';
import { RegisterPage } from './features/auth/RegisterPage';
import { ForgotPasswordPage } from './features/auth/ForgotPasswordPage';
import { DashboardPage } from './features/auth/DashboardPage';
import { ProfilePage } from './features/auth/ProfilePage';
import { ProfileSettingsPage } from './features/auth/ProfileSettingsPage';
import { ProfilePreferencesPage } from './features/auth/ProfilePreferencesPage';
import { BrokerAccountListPage } from './features/broker-accounts/BrokerAccountListPage';
import { AddBrokerAccountPage } from './features/broker-accounts/AddBrokerAccountPage';
import { BrokerRegistrationWizardPage } from './features/broker-accounts/BrokerRegistrationWizardPage';
import { BrokerIntegrationPage } from './features/broker-accounts/BrokerIntegrationPage';
import { BrokerAccountSettingsPage } from './features/broker-accounts/BrokerAccountSettingsPage';
import { SupportedBrokersPage } from './features/broker-accounts/SupportedBrokersPage';
import { PortfolioPage } from './features/portfolio/PortfolioPage';
import { PortfolioHoldingsPage } from './features/portfolio/PortfolioHoldingsPage';
import { PortfolioTransactionsPage } from './features/portfolio/PortfolioTransactionsPage';
import { PortfolioSyncPage } from './features/portfolio/PortfolioSyncPage';
import { MultiBrokerOrderForm } from './features/orders/MultiBrokerOrderForm';
import { OrderHistoryPage } from './features/orders/OrderHistoryPage';
import { AIAssistPage } from './features/ai-assist/AIAssistPage';
import { AIAssistStrategiesPage } from './features/ai-assist/AIAssistStrategiesPage';
import { AIAssistInsightsPage } from './features/ai-assist/AIAssistInsightsPage';
import { AnalyticsPage } from './features/analytics/AnalyticsPage';
import { AnalyticsReportsPage } from './features/analytics/AnalyticsReportsPage';
import { AnalyticsChartsPage } from './features/analytics/AnalyticsChartsPage';
import { ProtectedRoute } from './components/routing/ProtectedRoute';
import { CookiePolicyPage } from './features/policy/CookiePolicyPage';
import { DisclaimerPage } from './features/policy/DisclaimerPage';
import { PrivacyPolicyPage } from './features/policy/PrivacyPolicyPage';
import { DisclosureAgreementPage } from './features/policy/DisclosureAgreementPage';
import { TermsOfUsePage } from './features/policy/TermsOfUsePage';
import { AdminRoute } from './components/routing/AdminRoute';
import { AdminLandingPage } from './features/admin/AdminLandingPage';
import { AdminDashboardPage } from './features/admin/AdminDashboardPage';
import { AdminUsersPage } from './features/admin/AdminUsersPage';
import { AdminReferenceDataPage } from './features/admin/AdminReferenceDataPage';

function AppContent() {
  // Initialize auth state from storage on app load
  useAuthInit();

  React.useEffect(() => {
    // Open standard connection to our new market data multiplexer
    marketDataWS.connect();
    return () => {
      marketDataWS.disconnect();
    };
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />

        {/* Policy pages - Public */}
        <Route path="/policy/cookie" element={<CookiePolicyPage />} />
        <Route path="/policy/disclaimer" element={<DisclaimerPage />} />
        <Route path="/policy/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/policy/disclosure" element={<DisclosureAgreementPage />} />
        <Route path="/policy/terms" element={<TermsOfUsePage />} />

        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/settings"
          element={
            <ProtectedRoute>
              <ProfileSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/preferences"
          element={
            <ProtectedRoute>
              <ProfilePreferencesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/broker-accounts"
          element={
            <ProtectedRoute>
              <BrokerAccountListPage />
            </ProtectedRoute>
          }
        />
        {/* More specific path first so /broker-accounts/add/zerodha shows wizard, not grid */}
        <Route
          path="/broker-accounts/add/:brokerType"
          element={
            <ProtectedRoute>
              <BrokerRegistrationWizardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/broker-accounts/add"
          element={
            <ProtectedRoute>
              <AddBrokerAccountPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/broker-accounts/supported"
          element={
            <ProtectedRoute>
              <SupportedBrokersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/broker-accounts/settings"
          element={
            <ProtectedRoute>
              <BrokerAccountSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/broker-integration"
          element={
            <ProtectedRoute>
              <BrokerIntegrationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolio"
          element={
            <ProtectedRoute>
              <PortfolioPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolio/holdings"
          element={
            <ProtectedRoute>
              <PortfolioHoldingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolio/transactions"
          element={
            <ProtectedRoute>
              <PortfolioTransactionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolio/sync"
          element={
            <ProtectedRoute>
              <PortfolioSyncPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders/new"
          element={
            <ProtectedRoute>
              <MultiBrokerOrderForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders/history"
          element={
            <ProtectedRoute>
              <OrderHistoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders"
          element={
            <Navigate to="/orders/history" replace />
          }
        />
        <Route
          path="/ai-assist"
          element={
            <ProtectedRoute>
              <AIAssistPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-assist/strategies"
          element={
            <ProtectedRoute>
              <AIAssistStrategiesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-assist/insights"
          element={
            <ProtectedRoute>
              <AIAssistInsightsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <ProtectedRoute>
              <AnalyticsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics/reports"
          element={
            <ProtectedRoute>
              <AnalyticsReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics/charts"
          element={
            <ProtectedRoute>
              <AnalyticsChartsPage />
            </ProtectedRoute>
          }
        />

        {/* Admin routes — requires admin role (AdminRoute checks auth + isAdmin) */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminLandingPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/dashboard"
          element={
            <AdminRoute>
              <AdminDashboardPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <AdminRoute>
              <AdminUsersPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/reference-data"
          element={
            <AdminRoute>
              <AdminReferenceDataPage />
            </AdminRoute>
          }
        />

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/ai-assist" replace />} />

        {/* Catch all - redirect to ai-assist */}
        <Route path="*" element={<Navigate to="/ai-assist" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

// Material-UI theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#2196F3',
      dark: '#1565C0',
      light: '#64B5F6',
    },
    secondary: {
      main: '#0A2540',
    },
    success: {
      main: '#4CAF50',
    },
    error: {
      main: '#F44336',
    },
    warning: {
      main: '#FF9800',
    },
    background: {
      default: '#F5F5F5',
    },
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
});

function App() {
  return (
    <Provider store={store}>
      <DndProvider backend={HTML5Backend}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AppContent />
        </ThemeProvider>
      </DndProvider>
    </Provider>
  );
}

export default App;
